/**
 * Central mapping helpers between transport DTOs produced by the
 * LabFlow Web API controllers and the domain interfaces consumed
 * by pages. Page components must not import DTO types directly —
 * every repository passes its payload through one of the
 * functions below before returning.
 */
import type { Order, OrderStatus } from '@domain/models/order.interface';
import { OrderStatus as OS } from '@domain/models/order.interface';
import type { Invoice, InvoiceStatus } from '@domain/models/invoice.interface';
import { InvoiceStatus as IS } from '@domain/models/invoice.interface';
import type { Shipment, ShipmentStatus } from '@domain/models/shipment.interface';
import { ShipmentStatus as SS } from '@domain/models/shipment.interface';
import type { LabCase, CaseStatus } from '@domain/models/lab-case.interface';
import { CaseStatus as CS } from '@domain/models/lab-case.interface';
import type {
  AssignmentStatus,
  ProductionAssignment,
} from '@domain/models/production-assignment.interface';
import { AssignmentStatus as AS } from '@domain/models/production-assignment.interface';
import type { DeliveryNote, DeliveryNoteStatus } from '@domain/models/delivery-note.interface';
import { DeliveryNoteStatus as DNS } from '@domain/models/delivery-note.interface';
import type { Client } from '@domain/models/client.interface';
import type { Patient } from '@domain/models/patient.interface';
import type { WorkpieceType } from '@domain/models/workpiece-type.interface';
import type { Resource } from '@domain/models/resource.interface';
import type { Employee } from '@domain/models/employee.interface';
import type { LabRoute } from '@domain/models/lab-route.interface';
import type { ProductionPartner } from '@domain/models/production-partner.interface';
import type { DesignRequest, DesignRequestStatus } from '@domain/models/design-request.interface';
import { DesignRequestStatus as DRS } from '@domain/models/design-request.interface';
import type { PagedResult } from '@domain/models/paging.interface';
import { Status } from '@domain/models/status.enum';
import type { ClientOrder } from '@domain/models/client-order.interface';
import type { ClientInvoice } from '@domain/models/client-invoice.interface';
import type { ClientTimelineStep } from '@domain/models/client-timeline-step.interface';
import type {
  EmployeeNextStep,
  EmployeeTask,
  EmployeeTaskAttachment,
  EmployeeTaskDetail,
} from '@domain/models/employee-task.interface';

// --- Transport DTOs (mirror `LabFlow.Web.Dtos`) --------------------

export interface OrderDto {
  readonly id: string;
  readonly number: string;
  readonly customerName: string;
  readonly productLine: string;
  readonly status: string;
  readonly dueAt: string | null;
  readonly totalCents: number;
  readonly createdAt: string;
}

export interface InvoiceDto {
  readonly id: string;
  readonly number: string;
  readonly orderId: string;
  readonly customerName: string;
  readonly amountCents: number;
  readonly status: string;
  readonly issuedAt: string;
  readonly dueAt: string;
  readonly paidAt: string | null;
}

export interface ShipmentDto {
  readonly id: string;
  readonly orderId: string;
  readonly carrier: string;
  readonly trackingNumber: string | null;
  readonly status: string;
  readonly shippedAt: string | null;
  readonly deliveredAt: string | null;
  readonly recipient: string;
}

export interface CaseDto {
  readonly caseId: number | null;
  readonly caseNumber: string;
  readonly patientName: string | null;
  readonly state: number;
  readonly toothNumbers: string | null;
  readonly stepDescription: string | null;
  readonly units: number;
  readonly deliveryDate: string | null;
}

export interface ProductionAssignmentDto {
  readonly productionAssignmentId: number | null;
  readonly orderId: number | null;
  readonly assignedLabId: number | null;
  readonly productionPartnerId: number | null;
  readonly status: number;
  readonly assignedDate: string;
  readonly completedDate: string | null;
  readonly notes: string | null;
  readonly workpieceId?: string | null;
  readonly workpieceLabel?: string | null;
  readonly stepName?: string | null;
  readonly sequence?: number | null;
  readonly technicianName?: string | null;
  readonly completedAt?: string | null;
  readonly startedAt?: string | null;
}

export interface PagedDto<T> {
  readonly items: readonly T[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

// --- Status coercion ------------------------------------------------

const ORDER_STATUS_SET: ReadonlySet<string> = new Set(Object.values(OS));
const INVOICE_STATUS_SET: ReadonlySet<string> = new Set(Object.values(IS));
const SHIPMENT_STATUS_SET: ReadonlySet<string> = new Set(Object.values(SS));
const DELIVERY_NOTE_STATUS_SET: ReadonlySet<string> = new Set(Object.values(DNS));

function coerceOrderStatus(raw: string): OrderStatus {
  const v = raw.toLowerCase();
  return ORDER_STATUS_SET.has(v) ? (v as OrderStatus) : OS.Draft;
}
function coerceInvoiceStatus(raw: string): InvoiceStatus {
  const v = raw.toLowerCase();
  return INVOICE_STATUS_SET.has(v) ? (v as InvoiceStatus) : IS.Draft;
}
function coerceShipmentStatus(raw: string): ShipmentStatus {
  const v = raw.toLowerCase();
  return SHIPMENT_STATUS_SET.has(v) ? (v as ShipmentStatus) : SS.Pending;
}
function stateIntToStatus(state: number): CaseStatus {
  switch (state) {
    case 0: return CS.OrderReceived;
    case 1: return CS.InProduction;
    case 2: return CS.Shipped;
    case 3: return CS.Delivered;
    default: return CS.OrderReceived;
  }
}
function assignmentIntToStatus(value: number): AssignmentStatus {
  switch (value) {
    case 0: return AS.Assigned;
    case 1: return AS.Accepted;
    case 2: return AS.InProgress;
    case 3: return AS.Completed;
    default: return AS.Assigned;
  }
}
function shipmentStatusIntToDeliveryNoteStatus(value: number): DeliveryNoteStatus {
  switch (value) {
    case 0: return DNS.Pending;
    case 1: return DNS.Shipped;
    case 2: return DNS.Delivered;
    default: return DNS.Pending;
  }
}
function coerceDeliveryNoteStatus(raw: string): DeliveryNoteStatus {
  const v = raw.toLowerCase();
  return DELIVERY_NOTE_STATUS_SET.has(v) ? (v as DeliveryNoteStatus) : DNS.Pending;
}
function designRequestIntToStatus(value: number): DesignRequestStatus {
  switch (value) {
    case 0: return DRS.Open;
    case 1: return DRS.InProgress;
    case 2: return DRS.Completed;
    case 3: return DRS.Cancelled;
    default: return DRS.Open;
  }
}

// --- DTO → model ----------------------------------------------------

export function mapOrderDto(dto: OrderDto): Order {
  return {
    id: dto.id,
    number: dto.number,
    customer: dto.customerName,
    productLine: dto.productLine,
    status: coerceOrderStatus(dto.status),
    dueAt: dto.dueAt,
    totalCents: dto.totalCents,
    createdAt: dto.createdAt,
  };
}

export function mapInvoiceDto(dto: InvoiceDto): Invoice {
  return {
    id: dto.id,
    number: dto.number,
    orderId: dto.orderId,
    customer: dto.customerName,
    amountCents: dto.amountCents,
    status: coerceInvoiceStatus(dto.status),
    issuedAt: dto.issuedAt,
    dueAt: dto.dueAt,
    paidAt: dto.paidAt,
  };
}

export function mapShipmentDto(dto: ShipmentDto): Shipment {
  return {
    id: dto.id,
    orderId: dto.orderId,
    carrier: dto.carrier,
    trackingNumber: dto.trackingNumber,
    status: coerceShipmentStatus(dto.status),
    shippedAt: dto.shippedAt,
    deliveredAt: dto.deliveredAt,
    recipient: dto.recipient,
  };
}

export function mapCaseDto(dto: CaseDto): LabCase {
  return {
    id: dto.caseId != null ? String(dto.caseId) : '',
    caseNumber: dto.caseNumber,
    patientName: dto.patientName,
    toothNumbers: dto.toothNumbers,
    notes: dto.stepDescription,
    units: dto.units,
    deliveryDate: dto.deliveryDate,
    status: stateIntToStatus(dto.state),
  };
}

export function mapAssignmentDto(dto: ProductionAssignmentDto): ProductionAssignment {
  return {
    id: dto.productionAssignmentId != null ? String(dto.productionAssignmentId) : '',
    orderId: dto.orderId != null ? String(dto.orderId) : '',
    assignedLabId: dto.assignedLabId != null ? String(dto.assignedLabId) : null,
    productionPartnerId: dto.productionPartnerId != null ? String(dto.productionPartnerId) : null,
    status: assignmentIntToStatus(dto.status),
    assignedDate: dto.assignedDate,
    completedDate: dto.completedDate,
    notes: dto.notes,
    workpieceId: dto.workpieceId ?? '',
    workpieceLabel: dto.workpieceLabel ?? '',
    stepName: dto.stepName ?? '',
    sequence: dto.sequence ?? 0,
    technicianName: dto.technicianName ?? null,
    completedAt: dto.completedAt ?? null,
    startedAt: dto.startedAt ?? null,
  };
}

export function mapPaged<TDto, TModel>(
  dto: PagedDto<TDto>,
  mapItem: (item: TDto) => TModel,
): PagedResult<TModel> {
  return {
    items: dto.items.map(mapItem),
    total: dto.total,
    page: dto.page,
    pageSize: dto.pageSize,
  };
}

// --- Presentation status mapping ------------------------------------

/**
 * Maps an order's transport status + due-date into the canonical
 * presentation {@link Status} used by `StatusPill`, `TaskTile`, etc.
 */
export function orderPresentationStatus(order: Order, now: Date = new Date()): Status {
  if (order.status === OS.Cancelled) {
    return Status.Blocked;
  }
  if (order.status === OS.Ready || order.status === OS.Shipped) {
    return Status.Ready;
  }
  if (order.dueAt && new Date(order.dueAt).getTime() < now.getTime()) {
    return Status.Overdue;
  }
  if (order.status === OS.InProduction) {
    return Status.Progress;
  }
  return Status.New;
}

export function invoicePresentationStatus(
  invoice: Pick<Invoice, 'status' | 'dueAt'>,
  now: Date = new Date(),
): Status {
  if (invoice.status === IS.Paid) {
    return Status.Ready;
  }
  if (invoice.status === IS.Overdue) {
    return Status.Overdue;
  }
  if (invoice.status === IS.Draft) {
    return Status.New;
  }
  // Sent — check due date
  if (new Date(invoice.dueAt).getTime() < now.getTime()) {
    return Status.Overdue;
  }
  return Status.Progress;
}

export function shipmentPresentationStatus(shipment: Shipment): Status {
  switch (shipment.status) {
    case SS.Pending:
      return Status.New;
    case SS.InTransit:
      return Status.Progress;
    case SS.Delivered:
      return Status.Ready;
    case SS.Returned:
      return Status.Blocked;
    default:
      return Status.New;
  }
}

export function assignmentPresentationStatus(
  assignment: ProductionAssignment,
): Status {
  switch (assignment.status) {
    case AS.Assigned:
      return Status.New;
    case AS.Accepted:
      return Status.New;
    case AS.InProgress:
      return Status.Progress;
    case AS.Completed:
      return Status.Ready;
    default:
      return Status.New;
  }
}

// --- Client portal DTOs + mappers ----------------------------------

/**
 * Transport shape for `GET /api/client/orders[/:id]`. Mirrors
 * {@link OrderDto} but with the lab-internal `customerName`
 * replaced by a `patientRef` and without pricing internals
 * (`costCents`, technician breakdowns, etc.) that the lab
 * controller may include.
 */
export interface ClientOrderDto {
  readonly orderId: number;
  readonly orderNumber: number;
  readonly patientReference?: string;
  readonly productLine?: string;
  readonly status: string;
  readonly returnDate?: string | null;
  readonly totalCost: number;
  readonly orderDate: string;
}

export interface ClientTimelineStepDto {
  readonly id: string;
  readonly label: string;
  readonly status: string;
}

export interface ClientInvoiceDto {
  readonly id: string;
  readonly number: string;
  readonly orderId: string;
  readonly amountCents: number;
  readonly status: string;
  readonly issuedAt: string;
  readonly dueAt: string;
  readonly paidAt: string | null;
  readonly pdfUrl: string | null;
}

export interface ClientPortalDeliveryNoteDto {
  readonly id: string;
  readonly number: string;
  readonly orderId: string;
  readonly patientRef: string;
  readonly carrier: string;
  readonly trackingNumber: string | null;
  readonly status: string;
  readonly shippedAt: string | null;
  readonly deliveredAt: string | null;
  readonly pdfUrl: string | null;
}

export interface DeliveryNoteDto {
  readonly deliveryNoteId: number | null;
  readonly labId: number | null;
  readonly clientId: number | null;
  readonly orderId: number | null;
  readonly deliveryNoteNumber: string;
  readonly deliveryDate: string;
  readonly description: string | null;
  readonly shipmentStatus: number;
}

const STATUS_SET: ReadonlySet<string> = new Set(Object.values(Status));
function coercePresentationStatus(raw: string): Status {
  const v = raw.toLowerCase();
  return STATUS_SET.has(v) ? (v as Status) : Status.New;
}

/**
 * Maps the lab-internal {@link OrderDto} into the patient-anonymised
 * view-model the dentist portal uses. Strips every lab-only field
 * at the boundary so no pricing/cost/staffing data can reach a
 * dentist-role DOM.
 *
 * The `patientRef` is expected to be present on the dentist
 * variant of the endpoint (`/api/client/orders/…`). When pages
 * pull from the lab-facing `/api/orders`, we fall back to an
 * empty string rather than leak the lab customer name.
 */
export function mapClientOrderDto(dto: ClientOrderDto): ClientOrder {
  return {
    id: String(dto.orderId),
    number: String(dto.orderNumber),
    patientRef: dto.patientReference ?? '',
    productLine: dto.productLine ?? '',
    status: coerceOrderStatus(dto.status),
    dueAt: dto.returnDate ?? null,
    totalCents: Math.round(dto.totalCost * 100),
    createdAt: dto.orderDate,
  };
}

export function mapClientTimelineStepDto(
  dto: ClientTimelineStepDto,
): ClientTimelineStep {
  return {
    id: dto.id,
    label: dto.label,
    status: coercePresentationStatus(dto.status),
  };
}

export function mapClientInvoiceDto(dto: ClientInvoiceDto): ClientInvoice {
  return {
    id: dto.id,
    number: dto.number,
    orderId: dto.orderId,
    amountCents: dto.amountCents,
    status: coerceInvoiceStatus(dto.status),
    issuedAt: dto.issuedAt,
    dueAt: dto.dueAt,
    paidAt: dto.paidAt,
    pdfUrl: dto.pdfUrl,
  };
}

export function mapClientPortalDeliveryNoteDto(dto: ClientPortalDeliveryNoteDto): DeliveryNote {
  return {
    id: dto.id,
    deliveryNoteNumber: dto.number,
    orderId: dto.orderId,
    clientId: null,
    deliveryDate: dto.shippedAt ?? '',
    description: null,
    status: coerceDeliveryNoteStatus(dto.status),
    number: dto.number,
    patientRef: dto.patientRef,
    carrier: dto.carrier,
    trackingNumber: dto.trackingNumber,
    shippedAt: dto.shippedAt,
    deliveredAt: dto.deliveredAt,
    pdfUrl: dto.pdfUrl,
  };
}

export function mapDeliveryNoteDto(dto: DeliveryNoteDto): DeliveryNote {
  return {
    id: dto.deliveryNoteId != null ? String(dto.deliveryNoteId) : '',
    deliveryNoteNumber: dto.deliveryNoteNumber,
    orderId: dto.orderId != null ? String(dto.orderId) : null,
    clientId: dto.clientId != null ? String(dto.clientId) : null,
    deliveryDate: dto.deliveryDate,
    description: dto.description,
    status: shipmentStatusIntToDeliveryNoteStatus(dto.shipmentStatus),
    number: null,
    patientRef: null,
    carrier: null,
    trackingNumber: null,
    shippedAt: null,
    deliveredAt: null,
    pdfUrl: null,
  };
}

/**
 * Client-portal variant of {@link orderPresentationStatus}. Same
 * rules (cancelled → blocked, ready/shipped → ready, past-due →
 * overdue, in-production → progress, otherwise → new) but typed
 * against the anonymised {@link ClientOrder} so dentist pages
 * never touch the lab-facing `Order` interface.
 */
export function clientOrderPresentationStatus(
  order: ClientOrder,
  now: Date = new Date(),
): Status {
  if (order.status === OS.Cancelled) {
    return Status.Blocked;
  }
  if (order.status === OS.Ready || order.status === OS.Shipped) {
    return Status.Ready;
  }
  if (order.dueAt && new Date(order.dueAt).getTime() < now.getTime()) {
    return Status.Overdue;
  }
  if (order.status === OS.InProduction) {
    return Status.Progress;
  }
  return Status.New;
}


// --- Employee portal DTOs + mappers --------------------------------

export interface EmployeeTaskDto {
  readonly id: string;
  readonly orderId: string;
  readonly orderNumber: string;
  readonly patientRef: string;
  readonly workpieceId: string;
  readonly workpieceLabel: string;
  readonly stepName: string;
  readonly sequence: number;
  readonly station: string;
  readonly status: string;
  readonly dueAt: string | null;
  readonly barcode: string;
  readonly stepIndex?: number;
  readonly stepTotal?: number;
  readonly partner?: string | null;
  readonly completedAt?: string | null;
}

export interface EmployeeTaskAttachmentDto {
  readonly id: string;
  readonly name: string;
  readonly url: string;
  readonly thumbnailUrl: string | null;
}

export interface EmployeeTaskDetailDto {
  readonly task: EmployeeTaskDto;
  readonly notes: string | null;
  readonly attachments: readonly EmployeeTaskAttachmentDto[];
}

export interface EmployeeNextStepDto {
  readonly id: string;
  readonly name: string;
  readonly station: string;
}

export function mapEmployeeTaskDto(dto: EmployeeTaskDto): EmployeeTask {
  return {
    id: dto.id,
    orderId: dto.orderId,
    orderNumber: dto.orderNumber,
    patientRef: dto.patientRef,
    workpieceId: dto.workpieceId,
    workpieceLabel: dto.workpieceLabel,
    stepName: dto.stepName,
    sequence: dto.sequence,
    station: dto.station,
    status: coercePresentationStatus(dto.status),
    dueAt: dto.dueAt,
    barcode: dto.barcode,
    stepIndex: dto.stepIndex,
    stepTotal: dto.stepTotal,
    partner: dto.partner ?? null,
    completedAt: dto.completedAt ?? null,
  };
}

export function mapEmployeeTaskAttachmentDto(
  dto: EmployeeTaskAttachmentDto,
): EmployeeTaskAttachment {
  return {
    id: dto.id,
    name: dto.name,
    url: dto.url,
    thumbnailUrl: dto.thumbnailUrl,
  };
}

export function mapEmployeeTaskDetailDto(
  dto: EmployeeTaskDetailDto,
): EmployeeTaskDetail {
  return {
    task: mapEmployeeTaskDto(dto.task),
    notes: dto.notes,
    attachments: dto.attachments.map(mapEmployeeTaskAttachmentDto),
  };
}

export function mapEmployeeNextStepDto(dto: EmployeeNextStepDto): EmployeeNextStep {
  return {
    id: dto.id,
    name: dto.name,
    station: dto.station,
  };
}

// --- Lab library + marketplace DTOs + mappers ----------------------

export interface ClientDto {
  readonly clientId: number | null;
  readonly labId: number | null;
  readonly clientNumber: string;
  readonly name: string;
  readonly practiceType: string | null;
  readonly location: string | null;
  readonly address: string | null;
  readonly route: string | null;
  readonly dentalPracticeId: number | null;
  readonly isActive: boolean;
}

export interface PatientDto {
  readonly patientId: number | null;
  readonly clientId: number | null;
  readonly name: string;
  readonly initials: string | null;
  readonly dateOfBirth: string | null;
}

export interface WorkpieceTypeDto {
  readonly workpieceTypeId: number | null;
  readonly labId: number | null;
  readonly code: number;
  readonly description: string;
  readonly category: string | null;
  readonly isDigital: boolean;
  readonly isActive: boolean;
}

export interface ResourceDto {
  readonly resourceId: number | null;
  readonly labId: number | null;
  readonly code: string;
  readonly name: string;
  readonly abbreviation: string | null;
  readonly department: string | null;
  readonly isActive: boolean;
}

export interface EmployeeDto {
  readonly employeeId: number | null;
  readonly labId: number | null;
  readonly code: string;
  readonly name: string;
  readonly platformUserId: string | null;
}

export interface LabRouteDto {
  readonly routeId: number | null;
  readonly labId: number | null;
  readonly name: string;
}

export interface ProductionPartnerDto {
  readonly productionPartnerId: number | null;
  readonly sourceLabId: number | null;
  readonly partnerLabId: number | null;
  readonly specialization: string | null;
  readonly description: string | null;
  readonly isActive: boolean;
}

export interface DesignRequestDto {
  readonly designRequestId: number | null;
  readonly orderId: number | null;
  readonly caseId: number | null;
  readonly status: number;
  readonly designProvider: string | null;
  readonly requestedDate: string | null;
  readonly completedDate: string | null;
  readonly designFileUrl: string | null;
  readonly notes: string | null;
}

export function mapClientDto(dto: ClientDto): Client {
  return {
    id: dto.clientId != null ? String(dto.clientId) : '',
    clientNumber: dto.clientNumber,
    name: dto.name,
    practiceType: dto.practiceType,
    location: dto.location,
    address: dto.address,
    route: dto.route,
    isActive: dto.isActive,
  };
}

export function mapPatientDto(dto: PatientDto): Patient {
  return {
    id: dto.patientId != null ? String(dto.patientId) : '',
    name: dto.name,
    initials: dto.initials,
    clientId: dto.clientId != null ? String(dto.clientId) : '',
    dateOfBirth: dto.dateOfBirth,
  };
}

export function mapWorkpieceTypeDto(dto: WorkpieceTypeDto): WorkpieceType {
  return {
    id: dto.workpieceTypeId != null ? String(dto.workpieceTypeId) : '',
    code: dto.code,
    description: dto.description,
    category: dto.category,
    isDigital: dto.isDigital,
    isActive: dto.isActive,
  };
}

export function mapResourceDto(dto: ResourceDto): Resource {
  return {
    id: dto.resourceId != null ? String(dto.resourceId) : '',
    code: dto.code,
    name: dto.name,
    abbreviation: dto.abbreviation,
    department: dto.department,
    isActive: dto.isActive,
  };
}

export function mapEmployeeDto(dto: EmployeeDto): Employee {
  return {
    id: dto.employeeId != null ? String(dto.employeeId) : '',
    code: dto.code,
    name: dto.name,
  };
}

export function mapLabRouteDto(dto: LabRouteDto): LabRoute {
  return {
    id: dto.routeId != null ? String(dto.routeId) : '',
    name: dto.name,
  };
}

export function mapProductionPartnerDto(dto: ProductionPartnerDto): ProductionPartner {
  return {
    id: dto.productionPartnerId != null ? String(dto.productionPartnerId) : '',
    partnerLabId: dto.partnerLabId != null ? String(dto.partnerLabId) : null,
    specialization: dto.specialization,
    description: dto.description,
    isActive: dto.isActive,
  };
}

export function mapDesignRequestDto(dto: DesignRequestDto): DesignRequest {
  return {
    id: dto.designRequestId != null ? String(dto.designRequestId) : '',
    orderId: dto.orderId != null ? String(dto.orderId) : '',
    caseId: dto.caseId != null ? String(dto.caseId) : null,
    status: designRequestIntToStatus(dto.status),
    designProvider: dto.designProvider,
    requestedDate: dto.requestedDate,
    completedDate: dto.completedDate,
    notes: dto.notes,
  };
}
